import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Box, Paper, Typography, TextField, Button, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Tooltip, Divider
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const API = process.env.REACT_APP_API_URL;
const headersAuth = () => {
    const token = localStorage.getItem("token");
    return { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" };
};

const defaultValues = {
    id: null,
    nombre: "",
    apellido_1: "",
    apellido_2: "",
    nif: "",
    email: "",
    telefono_1: "",
    telefono_2: "",
    fecha_nacimiento: "",            // yyyy-MM-dd
    direccion: "",
    c_p_: "",
    localidad: "",
    provincia: "",
    pais: "",
    localidad_nacimiento: "",
    provincia_nacimiento: "",
    nacionalidad: "",
    nacionalidad2: "",
    observaciones: "",
    // nuevos
    iban: "",
    centro_trabajo: "",
    direccion_trabajo: "",
    poblacion_trabajo: "",
    c_p_trabajo: "",
    provincia_trabajo: "",
    telefono_trabajo: ""
};

const MaestroSchema = Yup.object({
    nombre: Yup.string().required("Requerido"),
    apellido_1: Yup.string().nullable(),
    apellido_2: Yup.string().nullable(),
    nif: Yup.string().required("Requerido"),
    email: Yup.string().email("Email inválido").nullable(),
    telefono_1: Yup.string().nullable(),
    telefono_2: Yup.string().nullable(),
    fecha_nacimiento: Yup.string().nullable(),
    c_p_: Yup.string().nullable(),
    direccion: Yup.string().nullable(),
    localidad: Yup.string().nullable(),
    provincia: Yup.string().nullable(),
    pais: Yup.string().nullable(),
    localidad_nacimiento: Yup.string().nullable(),
    provincia_nacimiento: Yup.string().nullable(),
    nacionalidad: Yup.string().nullable(),
    nacionalidad2: Yup.string().nullable(),
    observaciones: Yup.string().nullable(),
    // validaciones nuevas (suaves)
    iban: Yup.string()
        .nullable()
        .test("len", "IBAN inválido", v => !v || (v.replace(/\s|-/g, "").length >= 15 && v.replace(/\s|-/g, "").length <= 34)),
    centro_trabajo: Yup.string().nullable(),
    direccion_trabajo: Yup.string().nullable(),
    poblacion_trabajo: Yup.string().nullable(),
    c_p_trabajo: Yup.string().nullable(),
    provincia_trabajo: Yup.string().nullable(),
    telefono_trabajo: Yup.string().nullable()
});

function Filtros({ onBuscar, onNuevo, onExport, loading, valores }) {
    const [nombre, setNombre] = useState(valores?.nombre || "");
    const [nif, setNif] = useState(valores?.nif || "");
    const [email, setEmail] = useState(valores?.email || "");

    const handleBuscar = () => onBuscar({
        nombre: nombre || null,
        nif: nif || null,
        email: email || null
    });

    const handleLimpiar = () => {
        setNombre(""); setNif(""); setEmail("");
        onBuscar({ nombre: null, nif: null, email: null, limpiar: true });
    };

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h5" fontWeight={700}>Profesores</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={onExport} disabled={loading}>Exportar</Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={onNuevo}>Nuevo profesor</Button>
                </Box>
            </Box>

            <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={4}>
                    <TextField label="Nombre contiene..." value={nombre} onChange={(e) => setNombre(e.target.value)} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField label="NIF" value={nif} onChange={(e) => setNif(e.target.value)} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth size="small" />
                </Grid>
                <Grid item xs={12} display="flex" gap={1}>
                    <Button onClick={handleBuscar} variant="contained" disabled={loading}>Buscar</Button>
                    <Button onClick={handleLimpiar} variant="outlined" disabled={loading}>Limpiar</Button>
                </Grid>
            </Grid>
        </Paper>
    );
}

function DialogMaestro({ open, onClose, initialValues, onSubmit }) {
    const isEdit = Boolean(initialValues?.id);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? "Editar profesor" : "Nuevo profesor"}</DialogTitle>
            <Formik
                initialValues={initialValues || defaultValues}
                enableReinitialize
                validationSchema={MaestroSchema}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        const payload = { ...values };
                        Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
                        await onSubmit(payload);
                        onClose();
                    } finally { setSubmitting(false); }
                }}
            >
                {({ values, handleChange, touched, errors, isSubmitting }) => (
                    <Form>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="nombre" label="Nombre" value={values.nombre || ""} onChange={handleChange}
                                        fullWidth error={touched.nombre && Boolean(errors.nombre)} helperText={touched.nombre && errors.nombre} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="apellido_1" label="1º Apellido" value={values.apellido_1 || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="apellido_2" label="2º Apellido" value={values.apellido_2 || ""} onChange={handleChange} fullWidth />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField name="nif" label="NIF" value={values.nif || ""} onChange={handleChange}
                                        fullWidth error={touched.nif && Boolean(errors.nif)} helperText={touched.nif && errors.nif} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="email" label="Email" value={values.email || ""} onChange={handleChange}
                                        fullWidth error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email} />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="fecha_nacimiento" label="Fecha nacimiento" type="date" value={values.fecha_nacimiento || ""} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                                </Grid>

                                <Grid item xs={12} sm={8}>
                                    <TextField name="direccion" label="Dirección" value={values.direccion || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="c_p_" label="C.P." value={values.c_p_ || ""} onChange={handleChange} fullWidth />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField name="localidad" label="Localidad" value={values.localidad || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="provincia" label="Provincia" value={values.provincia || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="pais" label="País" value={values.pais || ""} onChange={handleChange} fullWidth />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField name="localidad_nacimiento" label="Localidad nacimiento" value={values.localidad_nacimiento || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="provincia_nacimiento" label="Provincia nacimiento" value={values.provincia_nacimiento || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="nacionalidad" label="Nacionalidad" value={values.nacionalidad || ""} onChange={handleChange} fullWidth />
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField name="nacionalidad2" label="Nacionalidad 2" value={values.nacionalidad2 || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="telefono_1" label="Teléfono 1" value={values.telefono_1 || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="telefono_2" label="Teléfono 2" value={values.telefono_2 || ""} onChange={handleChange} fullWidth />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        name="iban"
                                        label="IBAN"
                                        value={values.iban || ""}
                                        onChange={handleChange}
                                        fullWidth
                                        error={touched.iban && Boolean(errors.iban)}
                                        helperText={touched.iban && errors.iban}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>Datos de trabajo</Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField name="centro_trabajo" label="Centro" value={values.centro_trabajo || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField name="telefono_trabajo" label="Teléfono trabajo" value={values.telefono_trabajo || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField name="direccion_trabajo" label="Dirección trabajo" value={values.direccion_trabajo || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="poblacion_trabajo" label="Población trabajo" value={values.poblacion_trabajo || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="c_p_trabajo" label="C.P. trabajo" value={values.c_p_trabajo || ""} onChange={handleChange} fullWidth />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField name="provincia_trabajo" label="Provincia trabajo" value={values.provincia_trabajo || ""} onChange={handleChange} fullWidth />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField name="observaciones" label="Observaciones" value={values.observaciones || ""} onChange={handleChange} fullWidth multiline minRows={3} />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
                            <Button type="submit" variant="contained" disabled={isSubmitting}>
                                {isEdit ? "Guardar cambios" : "Crear profesor"}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}

export default function MaestrosAdmin() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [openDialog, setOpenDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [filtros, setFiltros] = useState({ nombre: null, nif: null, email: null });

    const fetchAll = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/api/maestros`, { headers: headersAuth() });
            setRows(data || []);
        } catch (e) {
            console.error(e); alert("No se pudieron cargar los maestros.");
        } finally { setLoading(false); }
    };

    const aplicarFiltros = (lista) => {
        const { nombre, nif, email } = filtros;
        return (lista || []).filter(m =>
            (!nombre || `${m.nombre || ""} ${m.apellido_1 || ""} ${m.apellido_2 || ""}`.toLowerCase().includes(nombre.toLowerCase())) &&
            (!nif || (m.nif || "").toLowerCase().includes(nif.toLowerCase())) &&
            (!email || (m.email || "").toLowerCase().includes(email.toLowerCase()))
        );
    };

    const buscar = async ({ nombre, nif, email, limpiar }) => {
        if (limpiar) {
            setFiltros({ nombre: null, nif: null, email: null });
            setPage(0);
            return;
        }
        setFiltros({ nombre, nif, email });
        setPage(0);
    };

    const handleCreate = () => { setEditing(null); setOpenDialog(true); };
    const handleEdit = (m) => { setEditing({ ...defaultValues, ...m, fecha_nacimiento: m.fecha_nacimiento ?? "" }); setOpenDialog(true); };

    const handleDelete = async (m) => {
        if (!window.confirm(`¿Eliminar a ${m.nombre} ${m.apellido_1 || ""}?`)) return;
        try {
            await axios.delete(`${API}/api/maestros/${m.id}`, { headers: headersAuth() });
            setRows(prev => prev.filter(r => r.id !== m.id));
        } catch (e) { console.error(e); alert("No se pudo eliminar el maestro."); }
    };

    const submitMaestro = async (payload) => {
        if (payload.id) {
            const { data } = await axios.put(`${API}/api/maestros/${payload.id}`, payload, { headers: headersAuth() });
            setRows(prev => prev.map(r => (r.id === payload.id ? data : r)));
        } else {
            const { data } = await axios.post(`${API}/api/maestros`, payload, { headers: headersAuth() });
            setRows(prev => [data, ...prev]);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const filtered = useMemo(() => aplicarFiltros(rows), [rows, filtros]);
    const paginated = useMemo(() => {
        const start = page * rowsPerPage;
        return filtered.slice(start, start + rowsPerPage);
    }, [filtered, page, rowsPerPage]);

    // ===== Exportación CSV (usa lista filtrada completa) =====
    const toCsvMaestros = (list) => {
        const header = [
            "ID", "Nombre", "Apellido 1", "Apellido 2", "NIF", "Email", "Teléfono 1", "Teléfono 2",
            "Fecha nacimiento", "Dirección", "C.P.", "Localidad", "Provincia", "País",
            "Localidad nacimiento", "Provincia nacimiento", "Nacionalidad", "Nacionalidad 2",
            "IBAN",
            "Centro trabajo", "Dirección trabajo", "Población trabajo", "C.P. trabajo", "Provincia trabajo", "Teléfono trabajo",
            "Observaciones"
        ];
        const lines = (list || []).map(m => {
            const vals = [
                m.id ?? "",
                m.nombre ?? "",
                m.apellido_1 ?? "",
                m.apellido_2 ?? "",
                m.nif ?? "",
                m.email ?? "",
                m.telefono_1 ?? "",
                m.telefono_2 ?? "",
                m.fecha_nacimiento ?? "",
                m.direccion ?? "",
                m.c_p_ ?? "",
                m.localidad ?? "",
                m.provincia ?? "",
                m.pais ?? "",
                m.localidad_nacimiento ?? "",
                m.provincia_nacimiento ?? "",
                m.nacionalidad ?? "",
                m.nacionalidad2 ?? "",
                m.iban ?? "",
                m.centro_trabajo ?? "",
                m.direccion_trabajo ?? "",
                m.poblacion_trabajo ?? "",
                m.c_p_trabajo ?? "",
                m.provincia_trabajo ?? "",
                m.telefono_trabajo ?? "",
                m.observaciones ?? ""
            ];
            return vals.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";");
        });
        return "\uFEFF" + [header.join(";"), ...lines].join("\n");
    };

    const handleExport = () => {
        const data = filtered;
        if (!data.length) { alert("No hay maestros para exportar."); return; }
        const csv = toCsvMaestros(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "maestros.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };
    // ========================================================

    return (
        <Box>
            <Filtros onBuscar={buscar} onNuevo={handleCreate} onExport={handleExport} loading={loading} valores={filtros} />

            <Paper sx={{ overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Nombre</TableCell>
                                <TableCell>NIF</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Teléfono</TableCell>
                                <TableCell>Localidad</TableCell>
                                <TableCell>Provincia</TableCell>
                                <TableCell>País</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((m) => (
                                <TableRow key={m.id}>
                                    <TableCell>{m.id}</TableCell>
                                    <TableCell>{[m.nombre, m.apellido_1, m.apellido_2].filter(Boolean).join(" ")}</TableCell>
                                    <TableCell>{m.nif || "-"}</TableCell>
                                    <TableCell>{m.email || "-"}</TableCell>
                                    <TableCell>{m.telefono_1 || "-"}</TableCell>
                                    <TableCell>{m.localidad || "-"}</TableCell>
                                    <TableCell>{m.provincia || "-"}</TableCell>
                                    <TableCell>{m.pais || "-"}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                        <Tooltip title="Editar"><IconButton onClick={() => handleEdit(m)} size="small"><EditIcon fontSize="small" /></IconButton></Tooltip>
                                        <Tooltip title="Eliminar"><IconButton onClick={() => handleDelete(m)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {paginated.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                            No hay maestros que mostrar.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filtered.length}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
            </Paper>

            <DialogMaestro
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                initialValues={editing || defaultValues}
                onSubmit={submitMaestro}
            />
        </Box>
    );
}
